import { Controller } from "@hotwired/stimulus"
import SignaturePad from 'signature_pad/dist/signature_pad.min.js';

export default class extends Controller {
  // Define targets that can be accessed within the controller
  static targets = ["input", "canvas", "name", "categories", "result", "waiting", "message", "form", "clear"]

  connect() {
    // Create an observer to monitor changes in provided element sizes
    this.resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        this.resizeCanvas();
      }
    });

    // Initialize a new SignaturePad instance on the element with the ID 'signature-pad'
    const canvas = this.canvasTarget;
    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: 'rgba(255, 255, 255, 0)',
      penColor: 'rgb(0, 0, 0)',
      throttle: 16, // Improve performance on mobile
      minWidth: 0.5,
      maxWidth: 2.5,
      onEnd: () => {
        // When the drawing ends, set the value of the element with the ID 'signature_input' to the data URL of the signature
        if (this.hasInputTarget) {
          this.inputTarget.value = this.signaturePad.toDataURL();
        }
      }
    });

    // Explicitly prevent default touch behaviors on canvas for webviews
    this._preventTouchDefault = (e) => e.preventDefault();
    canvas.addEventListener('touchstart', this._preventTouchDefault, { passive: false });
    canvas.addEventListener('touchmove', this._preventTouchDefault, { passive: false });
    // Initialize sizing and resize handler
    this._onResize = () => this.resizeCanvas();
    this.resizeCanvas(); // ensure correct size immediately
    window.addEventListener('resize', this._onResize);
    // 'resize' is only supported on 'window' objects, hence the ResizeObserver
    this.resizeObserver.observe(this.signaturePad.canvas.parentElement);
  }

  disconnect() {
    // Remove the resize observer on Stimulus disconnect to prevent memory leaks and to ensure the observer is not
    // triggered when the controller is no longer active.
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }

    const canvas = this.signaturePad?.canvas;
    if (canvas && this._preventTouchDefault) {
      canvas.removeEventListener('touchstart', this._preventTouchDefault);
      canvas.removeEventListener('touchmove', this._preventTouchDefault);
      this._preventTouchDefault = null;
    }
  }

  clear() {
    this.signaturePad.clear();
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
  }

  // Method to resize the canvas to match the width of its container
  resizeCanvas() {
    const canvas = this.signaturePad.canvas; // Get the canvas element from the signaturePad instance
    const container = canvas.parentNode; // Get the container element of the canvas

    // Set the canvas width to match the container's width
    canvas.width = container.offsetWidth;
  }
}
